import { diagnoseImageLoadError } from '@glyphic/shared-core'
import { type ImgHTMLAttributes,useCallback, useEffect, useMemo } from 'react'

import { type ReferenceImageItem } from '../api/referenceImages'
import { useToast } from '../context/ToastContext'
import { useBrokenImageHttpStatus } from '../hooks/useBrokenImageHttpStatus'
import { useImageInteractionPolicy } from '../hooks/useImageInteractionPolicy'
import { useImageUrlSelfHeal } from '../hooks/useImageUrlSelfHeal'
import { useTouchLongPress } from '../hooks/useLongPress'
import { toastForBrowserImageDownload, triggerBrowserImageDownload } from '../lib/clientImageDownload'
import { classifyReferenceImageLink, type ReferenceImageUrlHints } from '../lib/referenceImageUrl'
import { type RefreshUrlExecutor } from '../lib/referenceUrlRefresh'
import { resolveMediaImage } from '../lib/resolvedMediaImage'
import {
  buildImageContextMenuItems,
  ImageContextDesktopMenu,
  useImageContextPayload,
} from './ImageContextActions'
import {
  ImageLoadFailurePlaceholder,
  type ImageLoadFailurePlaceholderProps,
} from './ImageLoadFailurePlaceholder'

export type ThemedImageFallbackProps = ImageLoadFailurePlaceholderProps

/** @deprecated 使用 {@link ImageLoadFailurePlaceholder}；保留别名避免大范围改名 */
export function ThemedImageFallback(props: ThemedImageFallbackProps) {
  return <ImageLoadFailurePlaceholder {...props} />
}

export type ThemedImageProps = Omit<ImgHTMLAttributes<HTMLImageElement>, 'onError'> & {
  fallbackLabel?: string
  /** 落在占位组件上；默认复用 img 的 className */
  fallbackClassName?: string
  compact?: boolean
  angular?: boolean
  /** 供父组件在失败态禁用点击、清理选中等 */
  onFailedChange?: (failed: boolean) => void
  /** 移动端长按 / PC 右键菜单触发下载（策略见 useImageInteractionPolicy） */
  interactiveSave?: boolean
  /** 下载文件名前缀，默认可用 alt */
  downloadNameHint?: string
  /** 供 canvas 分析裁切（需 CDN 返回 CORS 头） */
  crossOrigin?: '' | 'anonymous' | 'use-credentials'
  /**
   * 开启「加载失败自动补签名」所需的对象键。
   * 有值时：图片裂了会先向后端要一份新签名再试，仍失败才展示错误占位；
   * 无值时：保持原有行为（失败即占位，占位上的按钮只是重新加载同一地址）。
   */
  objectKey?: string
  /** 地址解析 hints（bucket + objectKey），影响 remote-cdn 判定与 hochelatively 重写 */
  urlHints?: ReferenceImageUrlHints
  /** 自定义原始 URL → 展示 URL 转换（默认 {@link resolveMediaImage}.displayUrl） */
  transformUrl?: (rawUrl: string, hints?: ReferenceImageUrlHints) => string
  /** 自定义补签执行器；默认直接调后端 refresh-url */
  refreshExecutor?: RefreshUrlExecutor
  /** 补签成功后回调，供父级把新 url 回写列表状态 */
  onUrlRefreshed?: (item: ReferenceImageItem) => void
  /**
   * 极简失败占位：宿主自己已有一套失败 UI 时开启，
   * 避免同一格子里出现两个刷新按钮、两行「加载失败」。
   */
  minimalFallback?: boolean
}

/**
 * 加载失败时不保留原生裂图：切换为主题一致的占位（随全局 ThemeProvider 切换）。
 */
export function ThemedImage({
  className,
  alt = '',
  src,
  fallbackLabel: _unusedFallbackLabel,
  fallbackClassName,
  compact,
  angular = false,
  onFailedChange,
  interactiveSave,
  downloadNameHint,
  crossOrigin,
  objectKey,
  urlHints,
  transformUrl,
  refreshExecutor,
  onUrlRefreshed,
  minimalFallback,
  onLoad,
  ...rest
}: ThemedImageProps) {
  void _unusedFallbackLabel
  const { showToast } = useToast()
  const policy = useImageInteractionPolicy()
  const ctx = useImageContextPayload()

  /**
   * 地址自愈：失败先补签再重试（每张图最多自动补 {@link MAX_AUTO_HEALS} 次）。
   * 未传 objectKey 时退化为「失败 → 占位 → 手动重载同地址」的旧行为。
   */
  const heal = useImageUrlSelfHeal({
    rawUrl: src,
    objectKey,
    hints: urlHints,
    transformUrl: transformUrl ?? ((raw, hints) => resolveMediaImage(raw, hints).displayUrl),
    executor: refreshExecutor,
    onRefreshed: onUrlRefreshed,
  })
  const failed = heal.failed
  const displaySrc = heal.displayUrl
  const httpStatus = useBrokenImageHttpStatus(displaySrc, failed)
  /** 统一诊断：失败占位同时展示可读原因与资源地址（与 admin 端一致） */
  const loadErrorDiagnosis = useMemo(
    () => diagnoseImageLoadError({ url: displaySrc, httpStatus }),
    [displaySrc, httpStatus],
  )

  /**
   * 七牛 CDN 开了 Referer 防盗链（空 Referer 才放行，任意站点 Referer 一律 403）。
   * 远程 CDN 图片统一不发送 Referer；站内 /uploads 保持浏览器默认行为。
   */
  const referrerPolicy = useMemo(() => {
    if (rest.referrerPolicy) return rest.referrerPolicy
    if (!displaySrc) return undefined
    return classifyReferenceImageLink(displaySrc) === 'remote-cdn' ? 'no-referrer' : undefined
  }, [displaySrc, rest.referrerPolicy])

  const menuFileStem = useMemo(() => {
    const h = downloadNameHint?.trim().slice(0, 120) || alt?.trim().slice(0, 120)
    return h || `glyphic_${Date.now()}`
  }, [downloadNameHint, alt])

  useEffect(() => {
    onFailedChange?.(failed)
  }, [failed, onFailedChange])

  /** 占位上的重载：有 objectKey 时会先补一份新签名；否则只是同一地址重新加载一次 */
  const bumpReload = useCallback(() => {
    heal.retry()
  }, [heal])

  const trySaveToDisk = useCallback(async () => {
    if (!displaySrc || failed || !interactiveSave) return
    const stem =
      downloadNameHint?.trim().slice(0, 120) ||
      alt?.trim().slice(0, 120) ||
      `glyphic_${Date.now()}`
    try {
      const mode = await triggerBrowserImageDownload(displaySrc, stem)
      showToast(toastForBrowserImageDownload(mode))
    } catch (e) {
      showToast({
        message: e instanceof Error ? e.message : '保存失败',
        variant: 'error',
      })
    }
  }, [alt, displaySrc, failed, downloadNameHint, interactiveSave, showToast])

  const menuItems = useMemo(() => {
    if (!interactiveSave || !displaySrc) return []
    const payload = ctx.desktop ?? ctx.sheet
    if (!payload?.src) return []
    return buildImageContextMenuItems({
      imageUrl: displaySrc,
      onSave: async () => {
        await trySaveToDisk()
      },
      showToast,
    })
  }, [ctx.desktop, ctx.sheet, displaySrc, interactiveSave, showToast, trySaveToDisk])

  const lp = useTouchLongPress(() => void trySaveToDisk(), {
    disabled:
      !interactiveSave || failed || !src || !policy.syntheticLongPressSave,
  })

  useEffect(() => {
    if (!interactiveSave || !src) ctx.closeAll()
  }, [interactiveSave, src, ctx])

  if (failed) {
    return (
      <ImageLoadFailurePlaceholder
        angular={angular}
        compact={compact}
        minimal={minimalFallback}
        httpStatus={httpStatus}
        reason={loadErrorDiagnosis.reasonShort}
        urlLabel={loadErrorDiagnosis.safeUrlLabel}
        onReload={minimalFallback ? undefined : bumpReload}
        reloadBusy={heal.healing}
        className={fallbackClassName ?? className}
      />
    )
  }

  return (
    <>
      <img
        {...rest}
        {...lp}
        key={heal.reloadTick}
        src={displaySrc}
        alt={alt}
        crossOrigin={crossOrigin}
        referrerPolicy={referrerPolicy}
        className={className}
        onLoad={(e) => {
          heal.handleLoad()
          onLoad?.(e)
        }}
        onError={heal.handleError}
        onContextMenu={
          interactiveSave && displaySrc && !failed && policy.hijackContextMenu
            ? (e) => {
                e.preventDefault()
                ctx.openDesktop(e, displaySrc, menuFileStem)
              }
            : undefined
        }
      />
      {interactiveSave ? (
        <ImageContextDesktopMenu
          angular={false}
          open={ctx.desktop != null}
          anchor={
            ctx.desktop ? { x: ctx.desktop.x, y: ctx.desktop.y } : { x: 0, y: 0 }
          }
          items={menuItems}
          onClose={ctx.closeAll}
        />
      ) : null}
    </>
  )
}
